/**
 * ProButton - Premium Button Component
 * 
 * A meticulously crafted button component with:
 * - Gradient modes and glow effects
 * - 4 interaction states (idle, hover, active, focus)
 * - Loading, disabled, and celebration variants
 * - Sparkle animations and motion trails
 * - Full accessibility support
 * - Dual-shadow layers for retina displays
 * - Ultra-responsive design
 * - Bounce tap feedback
 * 
 * @component
 * @example
 * <ProButton variant="primary" size="lg" icon={<Phone />}>
 *   Call Lead
 * </ProButton>
 */

import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence, useMotionValue, useSpring } from 'framer-motion';
import { cn } from '@/lib/utils';
import { Loader2, Sparkles, CheckCircle2 } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { assertRenderableChildDeep, debugEnabled } from '@/lib/react/assertRenderableChild';
import { safeNode } from '@/lib/renderSafe';

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

export type ProButtonVariant = 
  | 'primary' 
  | 'secondary' 
  | 'outline' 
  | 'ghost' 
  | 'danger' 
  | 'success'
  | 'gradient'
  | 'glow';

export type ProButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export interface ProButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ProButtonVariant;
  size?: ProButtonSize;
  icon?: LucideIcon | React.ReactNode;
  iconPosition?: 'left' | 'right';
  loading?: boolean;
  disabled?: boolean;
  success?: boolean;
  sparkle?: boolean;
  glow?: boolean;
  gradient?: boolean;
  fullWidth?: boolean;
  children: React.ReactNode;
  onPress?: () => void;
  celebration?: boolean;
  motionTrail?: boolean;
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const getVariantClasses = (variant: ProButtonVariant, disabled: boolean, loading: boolean) => {
  const base = 'relative overflow-hidden font-semibold transition-all duration-300 ease-out';
  const disabledState = disabled || loading ? 'opacity-50 cursor-not-allowed pointer-events-none' : '';
  
  switch (variant) {
    case 'primary':
      return cn(
        base,
        'bg-primary text-primary-foreground',
        'hover:bg-primary/90 hover:shadow-lg hover:shadow-primary/20',
        'active:scale-[0.98] active:shadow-md',
        'focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        disabledState
      );
    
    case 'secondary':
      return cn(
        base,
        'bg-secondary text-secondary-foreground',
        'hover:bg-secondary/80 hover:shadow-md',
        'active:scale-[0.98]',
        'focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-2',
        disabledState
      );
    
    case 'outline':
      return cn(
        base,
        'border-2 border-border bg-transparent text-foreground',
        'hover:bg-accent hover:border-primary/50 hover:shadow-sm',
        'active:scale-[0.98] active:bg-accent/80',
        'focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
        disabledState
      );
    
    case 'ghost':
      return cn(
        base,
        'bg-transparent text-foreground',
        'hover:bg-accent hover:text-accent-foreground',
        'active:scale-[0.98] active:bg-accent/80',
        'focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
        disabledState
      );
    
    case 'danger':
      return cn(
        base,
        'bg-destructive text-destructive-foreground',
        'hover:bg-destructive/90 hover:shadow-lg hover:shadow-destructive/20',
        'active:scale-[0.98]',
        'focus-visible:ring-2 focus-visible:ring-destructive focus-visible:ring-offset-2',
        disabledState
      );
    
    case 'success':
      return cn(
        base,
        'bg-success text-success-foreground',
        'hover:bg-success/90 hover:shadow-lg hover:shadow-success/20',
        'active:scale-[0.98]',
        'focus-visible:ring-2 focus-visible:ring-success focus-visible:ring-offset-2',
        disabledState
      );
    
    case 'gradient':
      return cn(
        base,
        'bg-gradient-to-r from-primary via-primary/90 to-primary text-primary-foreground',
        'hover:from-primary/90 hover:via-primary/80 hover:to-primary/90',
        'hover:shadow-xl hover:shadow-primary/30',
        'active:scale-[0.98]',
        'focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
        disabledState
      );
    
    case 'glow':
      return cn(
        base,
        'bg-primary text-primary-foreground',
        'hover:shadow-[0_0_20px_hsl(var(--primary)/0.5)]',
        'active:scale-[0.98]',
        'focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
        disabledState
      );
    
    default:
      return base;
  }
};

const getSizeClasses = (size: ProButtonSize) => {
  switch (size) {
    case 'xs':
      return 'h-7 px-2.5 text-xs gap-1.5';
    case 'sm':
      return 'h-9 px-3 text-sm gap-2';
    case 'md':
      return 'h-10 px-4 text-base gap-2';
    case 'lg':
      return 'h-12 px-6 text-lg gap-2.5';
    case 'xl':
      return 'h-14 px-8 text-xl gap-3';
    default:
      return 'h-10 px-4 text-base gap-2';
  }
};

// ============================================================================
// SPARKLE ANIMATION COMPONENT
// ============================================================================

const SparkleParticle: React.FC<{ delay: number; x: number; y: number }> = ({ delay, x, y }) => {
  return (
    <motion.div
      className="absolute pointer-events-none"
      initial={{ opacity: 0, scale: 0, x, y }}
      animate={{
        opacity: [0, 1, 0],
        scale: [0, 1, 0],
        x: x + (Math.random() - 0.5) * 40,
        y: y + (Math.random() - 0.5) * 40,
      }}
      transition={{
        duration: 0.8,
        delay,
        ease: 'easeOut',
      }}
    >
      <Sparkles className="w-2 h-2 text-primary" />
    </motion.div>
  );
};

// ============================================================================
// CELEBRATION ANIMATION COMPONENT
// ============================================================================

const CelebrationEffect: React.FC<{ trigger: boolean }> = ({ trigger }) => {
  const particles = Array.from({ length: 12 }, (_, i) => ({
    id: i,
    angle: (i / 12) * 360,
    delay: i * 0.05,
  }));

  return (
    <AnimatePresence>
      {trigger && (
        <div className="absolute inset-0 pointer-events-none overflow-visible">
          {particles.map((particle) => {
            const rad = (particle.angle * Math.PI) / 180;
            const distance = 60;
            const x = Math.cos(rad) * distance;
            const y = Math.sin(rad) * distance;

            return (
              <motion.div
                key={particle.id}
                className="absolute top-1/2 left-1/2"
                initial={{ opacity: 0, scale: 0, x: 0, y: 0 }}
                animate={{
                  opacity: [0, 1, 0],
                  scale: [0, 1.2, 0],
                  x,
                  y,
                }}
                exit={{ opacity: 0 }}
                transition={{
                  duration: 0.6,
                  delay: particle.delay,
                  ease: 'easeOut',
                }}
              >
                <CheckCircle2 className="w-3 h-3 text-success" />
              </motion.div>
            );
          })}
        </div>
      )}
    </AnimatePresence>
  );
};

// ============================================================================
// MOTION TRAIL COMPONENT
// ============================================================================

const MotionTrail: React.FC<{ active: boolean }> = ({ active }) => {
  const trailLength = 5;
  const trails = Array.from({ length: trailLength }, (_, i) => i);

  return (
    <AnimatePresence>
      {active && (
        <div className="absolute inset-0 pointer-events-none overflow-visible">
          {trails.map((index) => (
            <motion.div
              key={index}
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/20"
              initial={{ width: 0, height: 0, opacity: 0.8 }}
              animate={{
                width: 40 + index * 10,
                height: 40 + index * 10,
                opacity: [0.8, 0.4, 0],
              }}
              exit={{ opacity: 0 }}
              transition={{
                duration: 0.5,
                delay: index * 0.1,
                ease: 'easeOut',
              }}
            />
          ))}
        </div>
      )}
    </AnimatePresence>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const ProButton = React.forwardRef<HTMLButtonElement, ProButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      icon: Icon,
      iconPosition = 'left',
      loading = false,
      disabled = false,
      success = false,
      sparkle = false,
      glow = false,
      gradient = false,
      fullWidth = false,
      children,
      className,
      onPress,
      celebration = false,
      motionTrail = false,
      onClick,
      ...props
    },
    ref
  ) => {
    // Debug tripwire: catch React error #31 (object rendered as child) in development
    if (debugEnabled() && children !== undefined) {
      try {
        assertRenderableChildDeep(children, "ProButton", "ProButton", 0);
      } catch (error) {
        console.error("[ProButton] Invalid child detected:", error);
        // Re-throw to see full stack trace
        throw error;
      }
    }

    const [isPressed, setIsPressed] = useState(false);
    const [sparkleTrigger, setSparkleTrigger] = useState(false);
    const [celebrationTrigger, setCelebrationTrigger] = useState(false);
    const [trailActive, setTrailActive] = useState(false);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const x = useMotionValue(0);
    const y = useMotionValue(0);
    const springX = useSpring(x, { stiffness: 300, damping: 30 });
    const springY = useSpring(y, { stiffness: 300, damping: 30 });

    // Handle mouse position for glow effect
    useEffect(() => {
      const handleMouseMove = (e: MouseEvent) => {
        if (!buttonRef.current || disabled || loading) return;
        
        const rect = buttonRef.current.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        
        x.set((e.clientX - centerX) / 10);
        y.set((e.clientY - centerY) / 10);
      };

      const button = buttonRef.current;
      if (button && glow) {
        button.addEventListener('mousemove', handleMouseMove);
        return () => button.removeEventListener('mousemove', handleMouseMove);
      }
    }, [glow, disabled, loading, x, y]);

    // Handle sparkle trigger
    useEffect(() => {
      if (sparkle && !disabled && !loading) {
        const interval = setInterval(() => {
          setSparkleTrigger(true);
          setTimeout(() => setSparkleTrigger(false), 1000);
        }, 3000);
        return () => clearInterval(interval);
      }
    }, [sparkle, disabled, loading]);

    // Handle success celebration
    useEffect(() => {
      if (success && !celebrationTrigger) {
        setCelebrationTrigger(true);
        setTimeout(() => setCelebrationTrigger(false), 1000);
      }
    }, [success, celebrationTrigger]);

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (disabled || loading) return;
      
      // Trigger motion trail
      if (motionTrail) {
        setTrailActive(true);
        setTimeout(() => setTrailActive(false), 500);
      }
      
      // Trigger celebration if enabled
      if (celebration) {
        setCelebrationTrigger(true);
        setTimeout(() => setCelebrationTrigger(false), 1000);
      }
      
      onClick?.(e);
      onPress?.();
    };

    const handleMouseDown = () => {
      if (!disabled && !loading) {
        setIsPressed(true);
      }
    };

    const handleMouseUp = () => {
      setIsPressed(false);
    };

    const handleMouseLeave = () => {
      setIsPressed(false);
      x.set(0);
      y.set(0);
    };

    // Determine final variant
    const finalVariant = gradient ? 'gradient' : glow ? 'glow' : variant;
    
    // Icon component handling
    // Only render function components or valid React elements
    // Never render plain objects (prevents React error #31)
    const IconComponent = typeof Icon === 'function' ? Icon : null;
    const IconNode = React.isValidElement(Icon) ? Icon : null;

    return (
      <motion.button
        ref={(node) => {
          if (typeof ref === 'function') ref(node);
          else if (ref) ref.current = node;
          buttonRef.current = node;
        }}
        className={cn(
          'relative inline-flex items-center justify-center',
          'rounded-lg',
          'select-none',
          'focus:outline-none',
          'disabled:cursor-not-allowed',
          getVariantClasses(finalVariant, disabled, loading),
          getSizeClasses(size),
          fullWidth && 'w-full',
          className
        )}
        onClick={handleClick}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        disabled={disabled || loading}
        whileTap={!disabled && !loading ? { scale: 0.96 } : {}}
        animate={{
          scale: isPressed && !disabled && !loading ? 0.96 : 1,
        }}
        transition={{
          type: 'spring',
          stiffness: 400,
          damping: 25,
        }}
        style={
          glow
            ? {
                x: springX,
                y: springY,
              }
            : undefined
        }
        {...props}
      >
        {/* Background gradient overlay for gradient variant */}
        {gradient && (
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-primary via-primary/90 to-primary opacity-100"
            animate={{
              backgroundPosition: ['0%', '100%', '0%'],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'linear',
            }}
          />
        )}

        {/* Glow effect overlay */}
        {glow && (
          <motion.div
            className="absolute inset-0 bg-primary/20 blur-xl opacity-0"
            animate={{
              opacity: isPressed ? 0.3 : 0,
            }}
            transition={{ duration: 0.2 }}
          />
        )}

        {/* Dual shadow layers for retina displays */}
        <div
          className={cn(
            'absolute inset-0 rounded-lg',
            'shadow-[0_2px_8px_rgba(0,0,0,0.1)]',
            'shadow-[0_1px_2px_rgba(0,0,0,0.05)]',
            variant === 'primary' && 'shadow-primary/10',
            variant === 'glow' && 'shadow-primary/20'
          )}
        />

        {/* Ripple effect on click */}
        <motion.div
          className="absolute inset-0 rounded-lg bg-white/20"
          initial={{ scale: 0, opacity: 0 }}
          animate={{
            scale: isPressed ? 1.5 : 0,
            opacity: isPressed ? [0.5, 0] : 0,
          }}
          transition={{ duration: 0.4 }}
        />

        {/* Content wrapper */}
        <span className="relative z-10 flex items-center justify-center">
          {/* Left icon */}
          {IconComponent && iconPosition === 'left' && !loading && (
            <IconComponent className="w-4 h-4" />
          )}
          {IconNode && iconPosition === 'left' && !loading && IconNode}

          {/* Loading spinner */}
          {loading && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
            >
              <Loader2 className="w-4 h-4 animate-spin" />
            </motion.div>
          )}

          {/* Success checkmark */}
          {success && !loading && (
            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              className="mr-2"
            >
              <CheckCircle2 className="w-4 h-4" />
            </motion.div>
          )}

          {/* Button text */}
          <span className={cn(loading && 'opacity-0', success && !loading && 'opacity-100')}>
            {safeNode(children)}
          </span>

          {/* Right icon */}
          {IconComponent && iconPosition === 'right' && !loading && (
            <IconComponent className="w-4 h-4" />
          )}
          {IconNode && iconPosition === 'right' && !loading && IconNode}
        </span>

        {/* Sparkle particles */}
        {sparkle && sparkleTrigger && buttonRef.current && (
          <div className="absolute inset-0 pointer-events-none overflow-visible">
            {Array.from({ length: 8 }).map((_, i) => (
              <SparkleParticle
                key={i}
                delay={i * 0.1}
                x={(Math.random() - 0.5) * 100}
                y={(Math.random() - 0.5) * 100}
              />
            ))}
          </div>
        )}

        {/* Celebration effect */}
        {celebration && <CelebrationEffect trigger={celebrationTrigger} />}

        {/* Motion trail */}
        {motionTrail && <MotionTrail active={trailActive} />}
      </motion.button>
    );
  }
);

ProButton.displayName = 'ProButton';

